import React from 'react';
import {
  Star,
  Redeem,
  EmojiEvents,
  LocalOffer,
  CardGiftcard,
  Favorite,
  LocationOn,
  AccessTime,
  Restaurant,
  ShoppingCart,
  AccountCircle,
  Notifications,
  Settings,
  Home,
  Search,
  Menu,
  TrendingUp,
  Whatshot,
  LocalFireDepartment,
  Grade,
  Category,
  Fastfood,
  LunchDining,
  DinnerDining,
  LocalPizza,
  LocalDining,
  Cake,
  Coffee,
  Liquor,
  RamenDining,
  SetMeal,
  TapasSharp,
  MenuBook,
  DeliveryDining,
  KebabDining,
  EmojiFoodBeverage,
  LocalBar,
  SoupKitchen,
  Storefront,
  Discount,
  NewReleases,
  Bolt,
} from '@mui/icons-material';

interface GetIconProps {
  iconName: string;
  color: string;
  className?: string;
}

/**
 * Get Material-UI icon component based on icon name
 * @param iconName - Name of the icon (case-insensitive)
 * @param color - Hex color for the icon
 * @param className - Optional CSS class name
 * @returns React element of the corresponding icon
 */
export const getIcon = ({ iconName, color, className = 'icon' }: GetIconProps): JSX.Element => {
  const iconProps = {
    className,
    style: { color }
  };

  const iconMap: { [key: string]: JSX.Element } = {
    // Rating & Favorites
    star: <Star {...iconProps} />,
    favorite: <Favorite {...iconProps} />,
    grade: <Grade {...iconProps} />,
    
    // Rewards & Offers
    redeem: <Redeem {...iconProps} />,
    trophy: <EmojiEvents {...iconProps} />,
    offer: <LocalOffer {...iconProps} />,
    gift: <CardGiftcard {...iconProps} />,
    discount: <Discount {...iconProps} />,
    
    // Food Categories
    restaurant: <Restaurant {...iconProps} />,
    fastfood: <Fastfood {...iconProps} />,
    lunch: <LunchDining {...iconProps} />,
    dinner: <DinnerDining {...iconProps} />,
    pizza: <LocalPizza {...iconProps} />,
    dining: <LocalDining {...iconProps} />,
    cake: <Cake {...iconProps} />,
    coffee: <Coffee {...iconProps} />,
    liquor: <Liquor {...iconProps} />,
    ramen: <RamenDining {...iconProps} />,
    setmeal: <SetMeal {...iconProps} />,
    tapas: <TapasSharp {...iconProps} />,
    menubook: <MenuBook {...iconProps} />,
    delivery: <DeliveryDining {...iconProps} />,
    kebab: <KebabDining {...iconProps} />,
    beverage: <EmojiFoodBeverage {...iconProps} />,
    bar: <LocalBar {...iconProps} />,
    soup: <SoupKitchen {...iconProps} />,
    storefront: <Storefront {...iconProps} />,
    
    // Trending & Popular
    trending: <TrendingUp {...iconProps} />,
    fire: <LocalFireDepartment {...iconProps} />,
    hot: <Whatshot {...iconProps} />,
    new: <NewReleases {...iconProps} />,
    bolt: <Bolt {...iconProps} />,
    
    // Navigation & UI
    cart: <ShoppingCart {...iconProps} />,
    account: <AccountCircle {...iconProps} />,
    notifications: <Notifications {...iconProps} />,
    settings: <Settings {...iconProps} />,
    home: <Home {...iconProps} />,
    search: <Search {...iconProps} />,
    menu: <Menu {...iconProps} />,
    category: <Category {...iconProps} />,
    
    // Location & Time
    location: <LocationOn {...iconProps} />,
    time: <AccessTime {...iconProps} />,
  };

  // Return the icon or default to star if not found
  return iconMap[iconName.toLowerCase()] || <Star {...iconProps} />;
};

/**
 * Get icon background colors based on primary color
 * @param primaryColor - Primary color in hex format
 * @returns Object with background color and light background color
 */
export const getIconBackgroundColors = (primaryColor: string): { bgColor: string; bgLight: string } => {
  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb = hexToRgb(primaryColor);
  
  if (rgb) {
    return {
      bgColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
      bgLight: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`
    };
  }
  
  // Fallback if hex conversion fails
  return {
    bgColor: `${primaryColor}15`,
    bgLight: `${primaryColor}10`
  };
};